/**
 * Reports Service
 *
 * Service for generating and managing skills gap reports
 */

import axios from 'axios';
import { API_BASE_URL } from '../config';
import { logger } from '../utils/logger';
import LocalStorageService from '../utils/localStorageService';
import { reportsTranslations } from '../utils/localization/translations/reports';
import { LANGUAGES } from '../utils/localization/config';

// Session token key
const SESSION_TOKEN_KEY = 'autobot_session_token';

// Helper function to get current language and translations
const getCurrentLanguageAndTranslations = () => {
  const storedLanguage = LocalStorageService.getItem('autobot_language') || LANGUAGES.FR;
  const translations = reportsTranslations[storedLanguage] || reportsTranslations[LANGUAGES.FR];
  return { currentLanguage: storedLanguage, t: translations };
};

/**
 * Make API call with proper authentication
 * @param {string} url - API endpoint URL
 * @param {Object} options - Request options
 * @returns {Promise<Object>} API response data
 */
async function apiCall(url, options = {}) {
  const sessionToken = LocalStorageService.getItem(SESSION_TOKEN_KEY);
  const config = {
    url: `${API_BASE_URL}${url}`,
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` }),
      ...options.headers
    },
    ...options
  };

  try {
    const response = await axios(config);
    return response.data;
  } catch (error) {
    logger.error('apiCall', `API call failed: ${url}`, error);
    throw error.response?.data || error;
  }
}

const REPORTS_API_BASE = '/reports';

class ReportsService {
  /**
   * Generate team skills gap report for manager
   * @param {string} managerId - Manager user ID
   * @returns {Promise<Object>} Team skills gap report
   */
  async generateTeamReport(managerId) {
    try {
      logger.info('ReportsService', `Generating team report for manager: ${managerId}`);

      const response = await apiCall(`${REPORTS_API_BASE}/team/${managerId}`, {
        method: 'GET'
      });

      logger.info('ReportsService', `Team report generated successfully for manager: ${managerId}`, {
        employeesAnalyzed: response.general_data?.total_employees_analyzed,
        averageMatchRate: response.key_indicators?.average_job_skill_match_rate,
        topGaps: response.top_missing_skills?.length
      });

      return response;
    } catch (error) {
      logger.error('ReportsService', `Error generating team report for manager ${managerId}`, error);
      throw new Error(`Failed to generate team report: ${error.message}`);
    }
  }

  /**
   * Generate company-wide skills gap report (admin only)
   * @returns {Promise<Object>} Company skills gap report
   */
  async generateCompanyReport() {
    try {
      logger.info('ReportsService', 'Generating company-wide skills gap report');

      const response = await apiCall(`${REPORTS_API_BASE}/company`, {
        method: 'GET'
      });

      logger.info('ReportsService', 'Company report generated successfully', {
        employeesAnalyzed: response.general_data?.total_employees_analyzed,
        averageMatchRate: response.key_indicators?.average_job_skill_match_rate,
        topGaps: response.top_missing_skills?.length
      });

      return response;
    } catch (error) {
      logger.error('ReportsService', 'Error generating company report', error);
      throw new Error(`Failed to generate company report: ${error.message}`);
    }
  }

  /**
   * Get detailed report data for specific user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User report data
   */
  async getUserReportData(userId) {
    try {
      logger.info('ReportsService', `Getting report data for user: ${userId}`);

      const response = await apiCall(`${REPORTS_API_BASE}/user/${userId}`, {
        method: 'GET'
      });

      logger.info('ReportsService', `User report data retrieved for: ${userId}`, {
        matchRate: response.match_rate,
        hasCV: response.has_cv_analysis,
        hasJob: response.has_job_analysis
      });

      return response;
    } catch (error) {
      logger.error('ReportsService', `Error getting report data for user ${userId}`, error);
      throw new Error(`Failed to get user report data: ${error.message}`);
    }
  }

  /**
   * Generate PDF report from report data
   * @param {Object} reportData - Report data object
   * @param {string} filename - PDF filename
   * @returns {Promise<Blob>} PDF blob for download
   */
  async generatePDFReport(reportData, filename = 'skills-gap-report.pdf') {
    try {
      logger.info('ReportsService', `Generating PDF report: ${filename}`, {
        reportDataKeys: Object.keys(reportData || {}),
        reportDataType: typeof reportData
      });

      // Enhanced jsPDF import with better error handling
      logger.info('ReportsService', 'Importing jsPDF library...');

      let jsPDF, autoTable;
      try {
        // Import jsPDF
        const jsPDFModule = await import('jspdf');
        jsPDF = jsPDFModule.default || jsPDFModule.jsPDF || jsPDFModule;
        logger.info('ReportsService', 'jsPDF imported successfully', { type: typeof jsPDF });

        // Import autotable plugin - this should automatically extend jsPDF
        const autoTableModule = await import('jspdf-autotable');
        autoTable = autoTableModule.default || autoTableModule;
        logger.info('ReportsService', 'jsPDF-autotable imported successfully', { type: typeof autoTable });

        // Apply the plugin to jsPDF if it hasn't been applied automatically
        if (autoTable && typeof autoTable === 'function' && jsPDF.prototype && !jsPDF.prototype.autoTable) {
          jsPDF.prototype.autoTable = autoTable;
          logger.info('ReportsService', 'AutoTable plugin applied to jsPDF prototype');
        }

        if (!jsPDF) {
          throw new Error('jsPDF import failed - jsPDF is undefined');
        }
      } catch (importError) {
        logger.error('ReportsService', 'Failed to import PDF libraries', importError);
        throw new Error(`PDF library import failed: ${importError.message}`);
      }

      logger.info('ReportsService', 'Creating PDF document...');
      const doc = new jsPDF();

      // Verify autotable plugin is available
      logger.info('ReportsService', 'Checking autoTable availability:', {
        hasAutoTable: typeof doc.autoTable === 'function',
        docMethods: Object.getOwnPropertyNames(doc).filter(name => name.includes('auto'))
      });

      // If autotable is not available, apply it manually
      if (typeof doc.autoTable !== 'function') {
        logger.info('ReportsService', 'AutoTable not found on instance, attempting manual application...');
        // Try to apply autotable manually to this instance
        if (autoTable && typeof autoTable === 'function') {
          // Method: Apply autotable correctly by binding it to the document
          doc.autoTable = function(options) {
            return autoTable(doc, options);
          };
          logger.info('ReportsService', 'AutoTable applied manually to document instance with fixed binding');
        } else {
          throw new Error('jsPDF autotable plugin failed to load properly. Please refresh the page and try again.');
        }
      } else {
        logger.info('ReportsService', 'AutoTable is available on document instance');
      }

      // Set up document properties
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = 20;

      // Get current language and translations
      const { currentLanguage, t } = getCurrentLanguageAndTranslations();
      logger.info('ReportsService', `Generating PDF in language: ${currentLanguage}`);

      // PDF-specific translations
      const pdfTranslations = {
        [LANGUAGES.FR]: {
          sections: {
            generalData: '1. Données générales',
            keyIndicators: '2. Indicateurs clés',
            domainBreakdown: '3. Répartition par domaine de compétences',
            topMissingSkills: '4. Principales compétences manquantes',
            trainingRecommendations: '5. Recommandations de formation',
            departmentBreakdown: '6. Répartition par département',
            conclusion: '7. Conclusion et prochaines étapes'
          },
          labels: {
            averageJobSkillMatchRate: 'Taux de correspondance emploi/compétence moyen',
            minMaxMatchRates: 'Taux de correspondance Min/Max',
            avgSkillsMasteredPerEmployee: 'Compétences maîtrisées/Employé (moy.)',
            avgMissingSkillsPerEmployee: 'Compétences manquantes/Employé (moy.)',
            keyInsights: 'Principales conclusions',
            priorityActions: 'Actions prioritaires'
          },
          tableHeaders: {
            domain: 'Domaine',
            avgMastered: '% Maîtrisé (Moy.)',
            avgMissing: '% Manquant (Moy.)',
            criticalSkills: 'Compétences critiques manquantes',
            rank: 'Rang',
            skillName: 'Nom de la compétence',
            missingCount: 'Nombre manquant',
            percentMissing: '% Manquant',
            requiredLevel: 'Niveau requis',
            priority: 'Priorité',
            skill: 'Compétence',
            urgency: 'Urgence',
            employeesAffected: 'Employés affectés',
            department: 'Département',
            employees: 'Employés',
            avgMatchRate: 'Taux corresp. (Moy.)',
            missingSkills: 'Compétences manquantes',
            topMissingSkills: 'Principales compétences manquantes'
          }
        },
        [LANGUAGES.EN]: {
          sections: {
            generalData: '1. General Data',
            keyIndicators: '2. Key Indicators',
            domainBreakdown: '3. Breakdown by Skill Domain',
            topMissingSkills: '4. Top Missing Skills Across All Departments',
            trainingRecommendations: '5. Training Recommendations',
            departmentBreakdown: '6. Breakdown by Department',
            conclusion: '7. Conclusion & Next Steps'
          },
          labels: {
            averageJobSkillMatchRate: 'Average Job/Skill Match Rate',
            minMaxMatchRates: 'Min/Max Match Rates',
            avgSkillsMasteredPerEmployee: 'Avg Skills Mastered/Employee',
            avgMissingSkillsPerEmployee: 'Avg Missing Skills/Employee',
            keyInsights: 'Key Insights',
            priorityActions: 'Priority Actions'
          },
          tableHeaders: {
            domain: 'Domain',
            avgMastered: 'Avg % Mastered',
            avgMissing: 'Avg % Missing',
            criticalSkills: 'Most Critical Missing Skills',
            rank: '#',
            skillName: 'Skill Name',
            missingCount: 'Missing Count',
            percentMissing: '% Missing',
            requiredLevel: 'Required Level',
            priority: 'Priority',
            skill: 'Skill',
            urgency: 'Urgency',
            employeesAffected: 'Employees Affected',
            department: 'Department',
            employees: 'Employees',
            avgMatchRate: 'Avg Match Rate',
            missingSkills: 'Missing Skills',
            topMissingSkills: 'Top Missing Skills'
          }
        }
      };

      const pt = pdfTranslations[currentLanguage] || pdfTranslations[LANGUAGES.EN];

      // Helper function to add new page if needed with proper padding
      const checkAddNewPage = (requiredSpace = 20) => {
        // Use more conservative bottom margin to prevent cutoff
        const bottomMargin = margin + 30; // Extra 30 units of padding at bottom
        if (yPos + requiredSpace > pageHeight - bottomMargin) {
          doc.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // Helper function for section headers
      const addSectionHeader = (title, prefix = '') => {
        checkAddNewPage(25);
        doc.setFillColor(240, 248, 255); // Light blue background
        doc.rect(margin, yPos - 5, contentWidth, 20, 'F');
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(33, 37, 41); // Dark gray
        // Remove emojis and use simple text prefixes
        const cleanTitle = prefix ? `${prefix} ${title}` : title;
        doc.text(cleanTitle, margin + 5, yPos + 8);
        yPos += 25;
      };

      // Helper function for statistics cards
      const addStatsCards = (stats, cardsPerRow = 3) => {
        const cardSpacing = 5;
        const cardWidth = (contentWidth - (cardSpacing * (cardsPerRow - 1))) / cardsPerRow;
        const cardHeight = 30;
        let xPos = margin;

        stats.forEach((stat, index) => {
          if (index % cardsPerRow === 0 && index > 0) {
            xPos = margin;
            yPos += cardHeight + 10;
          }

          // Card background with proper bounds checking
          if (xPos + cardWidth <= pageWidth - margin) {
            doc.setFillColor(248, 249, 250); // Light gray
            doc.setDrawColor(220, 220, 220); // Border color
            doc.rect(xPos, yPos, cardWidth, cardHeight, 'FD');

            // Value - centered in card
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            const color = stat.color || [33, 150, 243]; // Blue default
            doc.setTextColor(color[0], color[1], color[2]);
            const valueText = stat.value.toString();
            doc.text(valueText, xPos + cardWidth / 2, yPos + 12, { align: 'center' });

            // Label - centered below value with proper wrapping
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(108, 117, 125); // Gray
            const labelLines = doc.splitTextToSize(stat.label, cardWidth - 4);
            const startY = yPos + 20;
            labelLines.forEach((line, lineIndex) => {
              doc.text(line, xPos + cardWidth / 2, startY + (lineIndex * 3), { align: 'center' });
            });
          }

          xPos += cardWidth + cardSpacing;
        });

        yPos += cardHeight + 20;
      };

      // ===== TITLE PAGE =====
      // Main title with enhanced styling
      doc.setFillColor(13, 110, 253); // Bootstrap blue
      doc.rect(0, 0, pageWidth, 60, 'F');

      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255); // White text
      doc.text(t.skillsGapReport, pageWidth / 2, 30, { align: 'center' });

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(
        currentLanguage === LANGUAGES.FR ?
          'Analyse complète et recommandations de formation' :
          'Comprehensive Analysis & Training Recommendations',
        pageWidth / 2, 45, { align: 'center' }
      );

      yPos = 80;

      // Report metadata in styled box
      doc.setFillColor(248, 249, 250);
      doc.setDrawColor(220, 220, 220);
      doc.rect(margin, yPos, contentWidth, 40, 'FD');

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(33, 37, 41);
      const scopeLabel = currentLanguage === LANGUAGES.FR ? 'Portée du rapport' : 'Report Scope';
      const scopeValue = currentLanguage === LANGUAGES.FR ? 'Vue d\'équipe/entreprise' : 'Team/Company View';
      const generatedLabel = currentLanguage === LANGUAGES.FR ? 'Généré' : 'Generated';

      doc.text(`${scopeLabel}: ${reportData.scope || scopeValue}`, margin + 10, yPos + 12);
      doc.text(`${generatedLabel}: ${new Date(reportData.generated_date).toLocaleString()}`, margin + 10, yPos + 22);
      doc.text(`${t.preparedBy}: ${reportData.prepared_by || 'System'}`, margin + 10, yPos + 32);

      yPos += 60;

      // ===== SECTION 1: GENERAL DATA =====
      addSectionHeader(pt.sections.generalData, 'Section');

      const generalData = reportData.general_data || {};
      const generalStats = [
        {
          value: generalData.total_employees_in_system || 0,
          label: currentLanguage === LANGUAGES.FR ? 'Total employés dans le système' : 'Total Employees in System',
          color: [33, 150, 243] // Blue
        },
        {
          value: generalData.total_employees_analyzed || 0,
          label: currentLanguage === LANGUAGES.FR ? 'Employés avec données d\'analyse' : 'Employees with Analysis Data',
          color: [40, 167, 69] // Green
        },
        {
          value: generalData.total_job_descriptions_integrated || 0,
          label: currentLanguage === LANGUAGES.FR ? 'Descriptions d\'emploi intégrées' : 'Job Descriptions Integrated',
          color: [255, 193, 7] // Yellow
        },
        {
          value: generalData.total_required_skills || 0,
          label: currentLanguage === LANGUAGES.FR ? 'Total compétences requises' : 'Total Required Skills',
          color: [220, 53, 69] // Red
        },
        {
          value: generalData.total_skills_currently_held || 0,
          label: currentLanguage === LANGUAGES.FR ? 'Compétences actuellement détenues' : 'Skills Currently Held',
          color: [23, 162, 184] // Info
        },
        {
          value: generalData.total_missing_skills || 0,
          label: currentLanguage === LANGUAGES.FR ? 'Total compétences manquantes' : 'Total Missing Skills',
          color: [255, 87, 34] // Orange
        }
      ];

      addStatsCards(generalStats, 3);

      // ===== SECTION 2: KEY INDICATORS =====
      addSectionHeader(pt.sections.keyIndicators, 'Section');

      const keyIndicators = reportData.key_indicators || {};
      const keyStats = [
        {
          value: `${keyIndicators.average_job_skill_match_rate || 0}%`,
          label: pt.labels.averageJobSkillMatchRate,
          color: [40, 167, 69] // Green
        },
        {
          value: `${keyIndicators.minimum_match_rate || 0}% / ${keyIndicators.maximum_match_rate || 0}%`,
          label: pt.labels.minMaxMatchRates,
          color: [108, 117, 125] // Gray
        },
        {
          value: keyIndicators.average_skills_mastered_per_employee || 0,
          label: pt.labels.avgSkillsMasteredPerEmployee,
          color: [23, 162, 184] // Info
        },
        {
          value: keyIndicators.average_missing_skills_per_employee || 0,
          label: pt.labels.avgMissingSkillsPerEmployee,
          color: [220, 53, 69] // Red
        }
      ];

      addStatsCards(keyStats, 2);

      // ===== SECTION 3: BREAKDOWN BY SKILL DOMAIN =====
      addSectionHeader(pt.sections.domainBreakdown, 'Section');

      if (reportData.domain_breakdown && Object.keys(reportData.domain_breakdown).length > 0) {
        const domainRows = [];
        Object.entries(reportData.domain_breakdown).forEach(([domain, data]) => {
          domainRows.push([
            domain,
            `${data.mastered_percentage || 0}%`,
            `${data.missing_percentage || 0}%`,
            (data.most_critical_skills || []).slice(0, 3).join(', ') || 'None'
          ]);
        });

        doc.autoTable({
          startY: yPos,
          head: [[
            pt.tableHeaders.domain,
            pt.tableHeaders.avgMastered,
            pt.tableHeaders.avgMissing,
            pt.tableHeaders.criticalSkills
          ]],
          body: domainRows,
          styles: {
            fontSize: 9,
            cellPadding: 4,
            halign: 'left',
            valign: 'middle'
          },
          headStyles: {
            fillColor: [52, 58, 64],
            textColor: [255, 255, 255],
            fontSize: 10,
            fontStyle: 'bold'
          },
          alternateRowStyles: {
            fillColor: [248, 249, 250]
          },
          columnStyles: {
            0: { cellWidth: 40 }, // Domain
            1: { cellWidth: 25, halign: 'center' }, // Mastered %
            2: { cellWidth: 25, halign: 'center' }, // Missing %
            3: { cellWidth: 90 } // Critical Skills
          },
          margin: { left: 20, right: 20 },
          didDrawPage: function(data) {
            yPos = data.cursor.y + 20; // Extra space after tables
          },
          pageBreak: 'avoid',
          showHead: 'everyPage'
        });
      } else {
        doc.setFontSize(10);
        doc.setTextColor(108, 117, 125);
        doc.text('No domain breakdown data available.', 25, yPos);
        yPos += 15;
      }

      checkAddNewPage(25);

      // ===== SECTION 4: TOP MISSING SKILLS =====
      addSectionHeader(pt.sections.topMissingSkills, 'Section');

      if (reportData.top_missing_skills && reportData.top_missing_skills.length > 0) {
        const skillsRows = reportData.top_missing_skills.slice(0, 15).map((skill, index) => [
          (skill.rank || index + 1).toString(),
          skill.skill || 'Unknown Skill',
          `${skill.employees_affected_count || 0} employees`,
          `${skill.employees_affected_percentage || 0}%`,
          skill.required_level || 'Unknown'
        ]);

        doc.autoTable({
          startY: yPos,
          head: [[
            pt.tableHeaders.rank,
            pt.tableHeaders.skillName,
            pt.tableHeaders.missingCount,
            pt.tableHeaders.percentMissing,
            pt.tableHeaders.requiredLevel
          ]],
          body: skillsRows,
          styles: {
            fontSize: 9,
            cellPadding: 3,
            halign: 'left',
            valign: 'middle'
          },
          headStyles: {
            fillColor: [220, 53, 69],
            textColor: [255, 255, 255],
            fontSize: 10,
            fontStyle: 'bold'
          },
          alternateRowStyles: {
            fillColor: [253, 236, 236]
          },
          columnStyles: {
            0: { cellWidth: 15, halign: 'center' }, // Rank
            1: { cellWidth: 60 }, // Skill Name
            2: { cellWidth: 30, halign: 'center' }, // Missing Count
            3: { cellWidth: 25, halign: 'center' }, // Percentage
            4: { cellWidth: 50 } // Domain
          },
          margin: { left: 20, right: 20 },
          didDrawPage: function(data) {
            yPos = data.cursor.y + 20; // Extra space after tables
          },
          pageBreak: 'avoid',
          showHead: 'everyPage'
        });
      } else {
        doc.setFontSize(10);
        doc.setTextColor(108, 117, 125);
        doc.text('No missing skills data available.', 25, yPos);
        yPos += 15;
      }

      checkAddNewPage(25);

      // ===== SECTION 5: TRAINING RECOMMENDATIONS =====
      addSectionHeader(pt.sections.trainingRecommendations, 'Section');

      if (reportData.training_recommendations && reportData.training_recommendations.length > 0) {
        const trainingRows = reportData.training_recommendations.slice(0, 10).map((training, index) => [
          (training.rank || index + 1).toString(),
          training.skill || 'Unknown Skill',
          training.domain || 'General',
          training.priority || 'Standard',
          `${training.affected_employees_count || 0} employees`
        ]);

        doc.autoTable({
          startY: yPos,
          head: [[
            pt.tableHeaders.priority,
            pt.tableHeaders.skill,
            pt.tableHeaders.domain,
            pt.tableHeaders.urgency,
            pt.tableHeaders.employeesAffected
          ]],
          body: trainingRows,
          styles: {
            fontSize: 9,
            cellPadding: 3,
            halign: 'left',
            valign: 'middle'
          },
          headStyles: {
            fillColor: [40, 167, 69],
            textColor: [255, 255, 255],
            fontSize: 10,
            fontStyle: 'bold'
          },
          alternateRowStyles: {
            fillColor: [232, 247, 237]
          },
          columnStyles: {
            0: { cellWidth: 20, halign: 'center' }, // Priority
            1: { cellWidth: 50 }, // Skill
            2: { cellWidth: 35 }, // Training Type
            3: { cellWidth: 20, halign: 'center' }, // Score
            4: { cellWidth: 55, halign: 'center' } // Employees
          },
          margin: { left: 20, right: 20 },
          didDrawPage: function(data) {
            yPos = data.cursor.y + 20; // Extra space after tables
          },
          pageBreak: 'avoid',
          showHead: 'everyPage'
        });
      } else {
        doc.setFontSize(10);
        doc.setTextColor(108, 117, 125);
        doc.text('No training recommendations available.', 25, yPos);
        yPos += 15;
      }

      checkAddNewPage(25);

      // ===== SECTION 6: DEPARTMENT BREAKDOWN =====
      addSectionHeader(pt.sections.departmentBreakdown, 'Section');

      if (reportData.department_breakdown && Object.keys(reportData.department_breakdown).length > 0) {
        const departmentRows = [];
        Object.entries(reportData.department_breakdown).forEach(([dept, data]) => {
          departmentRows.push([
            dept,
            `${data.total_users || 0}`,
            `${data.average_match_rate || 0}%`,
            `${data.total_missing_skills || 0}`,
            data.most_critical_missing_skill || 'None'
          ]);
        });

        doc.autoTable({
          startY: yPos,
          head: [[
            pt.tableHeaders.department,
            pt.tableHeaders.employees,
            pt.tableHeaders.avgMatchRate,
            pt.tableHeaders.missingSkills,
            pt.tableHeaders.topMissingSkills
          ]],
          body: departmentRows,
          styles: {
            fontSize: 9,
            cellPadding: 3,
            halign: 'left',
            valign: 'middle'
          },
          headStyles: {
            fillColor: [255, 193, 7],
            textColor: [0, 0, 0],
            fontSize: 10,
            fontStyle: 'bold'
          },
          alternateRowStyles: {
            fillColor: [255, 248, 220]
          },
          columnStyles: {
            0: { cellWidth: 45 }, // Department
            1: { cellWidth: 25, halign: 'center' }, // Employees
            2: { cellWidth: 30, halign: 'center' }, // Match Rate
            3: { cellWidth: 30, halign: 'center' }, // Missing Skills
            4: { cellWidth: 50 } // Top Missing
          },
          margin: { left: 20, right: 20 },
          didDrawPage: function(data) {
            yPos = data.cursor.y + 20; // Extra space after tables
          },
          pageBreak: 'avoid',
          showHead: 'everyPage'
        });
      } else {
        doc.setFontSize(10);
        doc.setTextColor(108, 117, 125);
        doc.text('No department breakdown data available.', 25, yPos);
        yPos += 15;
      }

      checkAddNewPage(25);

      // ===== SECTION 7: CONCLUSION & NEXT STEPS =====
      addSectionHeader(pt.sections.conclusion, 'Section');

      // Create conclusion content box - ensure it fits within page
      checkAddNewPage(70);
      doc.setDrawColor(108, 117, 125);
      doc.setFillColor(248, 249, 250);
      doc.roundedRect(margin, yPos - 5, contentWidth, 60, 3, 3, 'FD');

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(33, 37, 41);

      doc.text(`${pt.labels.keyInsights}:`, margin + 5, yPos + 8);
      doc.setFontSize(10);
      doc.text(`• Average skill match rate: ${keyIndicators.average_job_skill_match_rate || 0}%`, margin + 10, yPos + 16);
      doc.text(`• Total missing skills identified: ${reportData.general_data?.total_missing_skills || 0}`, margin + 10, yPos + 24);
      doc.text(`• Employees requiring training: ${reportData.general_data?.total_employees_analyzed || 0}`, margin + 10, yPos + 32);

      doc.setFontSize(11);
      doc.text(`${pt.labels.priorityActions}:`, margin + 5, yPos + 42);
      doc.setFontSize(10);
      doc.text('• Focus on top missing skills in critical domains', margin + 10, yPos + 50);

      yPos += 70;

      // Add footer with generation timestamp at bottom of page
      const footerY = pageHeight - 20;
      doc.setFontSize(8);
      doc.setTextColor(108, 117, 125);
      doc.setFont('helvetica', 'italic');
      doc.text(`Report generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, margin, footerY);
      doc.text('Powered by Autobot Competency Analyzer v2.0', margin, footerY + 8);

      // Generate and return PDF blob
      logger.info('ReportsService', 'Converting PDF to blob...');
      const pdfBlob = doc.output('blob');

      if (!pdfBlob || pdfBlob.size === 0) {
        throw new Error('PDF generation failed - empty blob generated');
      }

      logger.info('ReportsService', `PDF report generated successfully: ${filename}`, {
        blobSize: pdfBlob.size,
        blobType: pdfBlob.type
      });
      return pdfBlob;

    } catch (error) {
      logger.error('ReportsService', `Error generating PDF report: ${filename}`, error);
      throw new Error(`Failed to generate PDF report: ${error.message}`);
    }
  }

  /**
   * Download PDF report
   * @param {Object} reportData - Report data object
   * @param {string} filename - PDF filename
   */
  async downloadPDFReport(reportData, filename = 'skills-gap-report.pdf') {
    try {
      logger.info('ReportsService', `Starting PDF download process: ${filename}`);

      const pdfBlob = await this.generatePDFReport(reportData, filename);

      if (!pdfBlob) {
        throw new Error('PDF generation returned no blob');
      }

      logger.info('ReportsService', 'Creating download link...', {
        blobSize: pdfBlob.size,
        filename: filename
      });

      // Create download link and trigger download
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none'; // Hide the link
      document.body.appendChild(link);

      logger.info('ReportsService', 'Triggering download...');
      link.click();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        logger.info('ReportsService', 'Download link cleaned up');
      }, 100);

      logger.info('ReportsService', `PDF report download initiated successfully: ${filename}`);

    } catch (error) {
      logger.error('ReportsService', `Error downloading PDF report: ${filename}`, {
        error: error.message,
        stack: error.stack,
        reportDataExists: !!reportData
      });
      throw error;
    }
  }
}

// Create and export singleton instance
const reportsService = new ReportsService();
export default reportsService;
